import React from 'react';

import { Box, Card, Group, Text, useMantineTheme } from '@mantine/core';

import { formatEventTime, resolveCardVisuals } from './resolveCardVisuals';
import WorkoutVisibilityIcon from '../../WorkoutVisibilityIcon';
import type { AthleteCalendarItem } from '../types';

const NAME_MAX_LINES = 3;

interface WeekCardProps {
  item: AthleteCalendarItem;
  onClick: (item: AthleteCalendarItem) => void;
  menu?: React.ReactNode;
}

const WeekCard = ({ item, onClick, menu }: WeekCardProps): JSX.Element => {
  const theme = useMantineTheme();
  const { accent, tint, fill, foreground, isWorkout, isCompleted, isHidden, subtitle } = resolveCardVisuals(
    item,
    theme,
  );

  return (
    <Card
      p={8}
      radius='md'
      data-cy={`athleteCalendarCard-${item.kind}`}
      data-source={item.kind === 'workout' ? item.source : undefined}
      onClick={() => onClick(item)}
      sx={{
        backgroundColor: fill ?? (isCompleted ? tint : theme.colors.gray[0]),
        color: foreground,
        border: fill ? 'none' : `1px solid ${theme.colors.gray[2]}`,
        borderLeft: `4px solid ${accent}`,
        cursor: 'pointer',
        transition: 'box-shadow 150ms ease',
        '&:hover': { boxShadow: theme.shadows.sm },
      }}
    >
      <Group spacing={6} noWrap align='flex-start'>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Text
            size='xs'
            fw={700}
            lineClamp={isWorkout ? NAME_MAX_LINES : 1}
            sx={{ color: foreground, overflowWrap: 'break-word', hyphens: 'auto' }}
          >
            {item.name}
          </Text>

          {subtitle && (
            <Text
              size='xs'
              lineClamp={1}
              sx={{ color: theme.colors.gray[6] }}
              data-cy='athleteCalendarCardClass'
            >
              {subtitle}
            </Text>
          )}

          {isHidden && <WorkoutVisibilityIcon visible={false} withinPortal />}

          {item.kind !== 'workout' && (
            <Text size='xs' sx={{ color: foreground, opacity: 0.9 }}>
              {formatEventTime(item.startTime)}
            </Text>
          )}
        </Box>

        {menu}
      </Group>
    </Card>
  );
};

export default WeekCard;
